import { TreeItemProps } from "@mui/x-tree-view/TreeItem";
import { TreeItemProvider, useTreeItem, useTreeItemModel } from "@mui/x-tree-view-pro";
import clsx from "clsx";
import { omit } from "ramda";
import type { MouseEventHandler } from "react";
import React, { forwardRef, ReactElement, ReactNode, useCallback, useMemo, useTransition } from "react";
import { useTranslation } from "react-i18next";
import { ConditionElseBetweenLine } from "../BuilderTree/components/ConditionElseBetweenLine";
import { ElseBox } from "../BuilderTree/components/ElseBox";
import { RateBuilderTreeGroupTransition } from "../BuilderTree/components/RateBuilderTreeGroupTransition";
import { rateBuilderClasses, RateBuilderTreeItemRoot } from "../BuilderTree/components/RateBuilderTreeItemRoot";
import { useTreeDnD } from "@sinch/components/application/RatelistBuilder/BuilderTree/TreeDnDProvider";
import { CollapsibleBottomToolbar } from "@sinch/components/application/RatelistBuilder/RateTreeItem/CollapsibleBottomToolbar";
import { RateBuilderRow } from "@sinch/components/application/RatelistBuilder/RateTreeItem/RateBuilderRow";
import { RateTreeItemEmptyContentInteractionLayer } from "@sinch/components/application/RatelistBuilder/RateTreeItem/RateTreeItemEmptyContentInteractionLayer";
import { CollapsedRateBuilderBottomToolbar } from "@sinch/components/application/RatelistBuilder/RateTreeItem/RowFooter/CollapsedRateBuilderBottomToolbar";
import {
  DropOverlay,
  DropProgress,
  ExpandedEnvelope,
} from "@sinch/components/application/RatelistBuilder/RateTreeItem/styled";

import { RateBuilderTreeViewItem } from "@sinch/components/application/RatelistBuilder/types";
import { useRateBuilderSettings } from "@sinch/components/application/RatelistBuilder/UtilitiesProviders/RateBuilderSettingsProvider";
import { findItemInTree } from "@sinch/components/application/RatelistBuilder/utils/treeUtils";

export interface RateBuilderTreeItemProps extends Omit<TreeItemProps, "title" | "content"> {
  content: ReactElement | string;
  bottomSiblingToolsBar?: ReactNode;
  bottomNestedToolsBar?: ReactNode;
  onContextMenu?: MouseEventHandler<any> | undefined;
}

export const RateBuilderTreeItem = forwardRef(
  (
    { content, bottomSiblingToolsBar, bottomNestedToolsBar, onContextMenu, ...props }: RateBuilderTreeItemProps,
    ref: React.Ref<HTMLLIElement>
  ) => {
    const { t } = useTranslation();
    const [isPending, startTransition] = useTransition();
    const { disableNesting, mode } = useRateBuilderSettings();

    const {
      getRootProps,
      getContentProps,
      getLabelProps,
      getGroupTransitionProps,
      getIconContainerProps,
      status,
      publicAPI,
    } = useTreeItem({ ...props, rootRef: ref });

    const type = useTreeItemModel<RateBuilderTreeViewItem>(props.itemId)?.type ?? undefined;
    const { in: expanded } = getGroupTransitionProps();

    const itemTree = publicAPI.getItemTree();

    const item = useMemo(
      () => findItemInTree<RateBuilderTreeViewItem>(itemTree, props.itemId),
      [itemTree, props.itemId]
    );
    if (!item) {
      throw new Error("Item not found");
    }
    const { parent, position, index } = item;

    const isWrapped = parent?.type === "wrapper";
    const isOnlyChild = parent?.children?.length === 1;
    const isLast = isOnlyChild || position === "last";
    const isExpanded = type !== "wrapper" && status.expandable && status.expanded;

    // oxlint-disable-next-line @typescript-eslint/no-unused-vars
    const { draggable, onDragStart, onDragOver, onDragEnd, onKeyDown, ...rootProps } = getRootProps({
      className: clsx(
        type === "wrapper" && rateBuilderClasses.wrapper,
        !isWrapped && rateBuilderClasses.entryRate,
        isWrapped && rateBuilderClasses.ifElseItem,
        (mode === "view" || mode === "override") && rateBuilderClasses.overview
      ),
    });

    const { dropOverlayRef, loading } = useTreeDnD({ dropzoneItemId: item.parent?.id ?? null, index });
    const { dropOverlayRef: downDropOverlayRef, loading: downLoading } = useTreeDnD({
      dropzoneItemId: item.parent?.id ?? null,
      index: index + 1,
    });

    const handleExpand = useCallback(
      (expandProps: { event: React.SyntheticEvent; itemId: string; shouldBeExpanded: boolean }) => {
        if (expandProps.shouldBeExpanded) {
          startTransition(() => {
            publicAPI.setItemExpansion(expandProps);
          });
        } else {
          publicAPI.setItemExpansion(expandProps);
        }
      },
      [publicAPI]
    );

    const onCollapsedRateBuilderBottomToolbarExpand = useCallback(
      (e: React.SyntheticEvent) =>
        handleExpand({
          event: e,
          itemId: props.itemId,
          shouldBeExpanded: true,
        }),
      [handleExpand, props.itemId]
    );

    return (
      <TreeItemProvider id={props.itemId} itemId={props.itemId}>
        <RateBuilderTreeItemRoot {...omit(["onFocus", "onBlur"], rootProps)} expanded={isExpanded}>
          {mode === "edit" && <DropOverlay ref={dropOverlayRef}></DropOverlay>}
          {loading && <DropProgress />}
          <ExpandedEnvelope isExpanded={isExpanded} isWrapper={type === "wrapper"}>
            {type === "wrapper" ? (
              <RateTreeItemEmptyContentInteractionLayer {...getContentProps()} />
            ) : (
              <RateBuilderRow<RateBuilderTreeViewItem>
                contentProps={getContentProps()}
                iconContainerProps={getIconContainerProps()}
                isWrapped={isWrapped}
                item={item}
                labelProps={getLabelProps({ sx: { overflow: "inherit" } })}
                onContextMenu={mode === "view" || mode === "override" ? undefined : onContextMenu}
                onItemExpandToggle={handleExpand}
                rootClassName={clsx(
                  !isWrapped && rateBuilderClasses.entryRateContent,
                  item.position === "first" && isWrapped && rateBuilderClasses.ifElseItemContent
                )}
                status={status}
              >
                {content}
              </RateBuilderRow>
            )}
            {!disableNesting && (
              <>
                {props.children && (
                  <RateBuilderTreeGroupTransition
                    wrapped={type === "wrapper"}
                    {...getGroupTransitionProps()}
                    in={type === "wrapper" || expanded || !props.children}
                    mountOnEnter
                    unmountOnExit
                  >
                    {isWrapped && <ConditionElseBetweenLine></ConditionElseBetweenLine>}
                    {getGroupTransitionProps().children}
                  </RateBuilderTreeGroupTransition>
                )}
                {mode === "edit" && (
                  <CollapsibleBottomToolbar
                    expanded={expanded || type === "wrapper"}
                    isElseToolbar={type === "wrapper"}
                    isWrapped={isWrapped}
                  >
                    {bottomSiblingToolsBar}
                  </CollapsibleBottomToolbar>
                )}

                {isWrapped && !isOnlyChild && position !== "last" && (
                  <ElseBox boxPosition="bottom">{t("else")}</ElseBox>
                )}
              </>
            )}
          </ExpandedEnvelope>

          {!disableNesting && (mode === "edit" || Boolean(item.item.children?.length)) && (
            <CollapsedRateBuilderBottomToolbar
              childrenCount={item.item.children?.length ?? 0}
              expanded={expanded || type === "wrapper"}
              isPending={isPending}
              isWrapped={isWrapped}
              isWrapper={type === "wrapper"}
              onExpand={onCollapsedRateBuilderBottomToolbarExpand}
            >
              {bottomNestedToolsBar}
            </CollapsedRateBuilderBottomToolbar>
          )}

          {mode === "edit" && isLast && <DropOverlay ref={downDropOverlayRef}></DropOverlay>}
          {mode === "edit" && isLast && downLoading && <DropProgress />}
        </RateBuilderTreeItemRoot>
      </TreeItemProvider>
    );
  }
);
RateBuilderTreeItem.displayName = "RateBuilderTreeItem";
